import React from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { TrendingUp, TrendingDown } from 'lucide-react';

function AnimatedStatCard({ title, value, icon, change, changeType, description, delay = 0 }) {
  // Parse the value as a number if it has a prefix like $ or %
  const numericValue = typeof value === 'string'
    ? parseFloat(value.replace(/[^0-9.-]+/g, ''))
    : value;
  
  // Determine if the value has a currency or percent symbol
  const prefix = typeof value === 'string' && value.startsWith('$') ? '$' : '';
  const suffix = typeof value === 'string' && value.endsWith('%') ? '%' : '';
  
  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6, 
        delay: delay * 0.2, 
        ease: "easeOut" 
      }
    }
  };
  
  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        duration: 0.5, 
        delay: delay * 0.2 + 0.3, 
        ease: "easeOut" 
      }
    }
  };
  
  return (
    <motion.div 
      className="bg-white rounded-lg shadow-sm p-6 overflow-hidden relative"
      variants={cardVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="flex justify-between">
        <div>
          <motion.p 
            className="text-sm font-medium text-gray-600"
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: delay * 0.2 + 0.2, duration: 0.4 }}
          >
            {title}
          </motion.p>
          
          <div className="text-2xl font-bold mt-1">
            <CountUp 
              start={0}
              end={numericValue}
              duration={2}
              delay={delay * 0.2 + 0.3}
              decimals={numericValue % 1 === 0 ? 0 : 2}
              prefix={prefix}
              suffix={suffix}
              separator=","
            />
          </div>
          
          {change && (
            <motion.div 
              className="flex items-center mt-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: delay * 0.2 + 0.6, duration: 0.4 }}
            >
              {changeType === 'increase' ? (
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
              )}
              <span 
                className={
                  changeType === 'increase' 
                    ? 'text-sm font-medium text-green-600' 
                    : 'text-sm font-medium text-red-600'
                }
              >
                {change}
              </span>
            </motion.div>
          )}
          
          {description && (
            <motion.p 
              className="text-sm text-gray-500 mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: delay * 0.2 + 0.7, duration: 0.4 }}
            >
              {/* {description} */}
            </motion.p>
          )}
        </div>
        
        <motion.div 
          className="bg-primary-50 p-3 rounded-full"
          variants={iconVariants}
          initial="hidden"
          animate="visible"
        >
          {icon}
        </motion.div>
      </div>
      
      {/* Background decoration */}
      <motion.div 
        className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-primary-100 opacity-30"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.3 }}
        transition={{ delay: delay * 0.2 + 0.4, duration: 0.6 }}
      />
    </motion.div>
  );
}

export default AnimatedStatCard;